import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { type IIconsVector, type IRow } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { type RowInbox } from 'entities/Inbox/model/Table/RowInbox'
import { useInboxesViewContext } from 'pages/settings/pages/inboxes/context/inboxesViewContext'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { InboxesTable } from 'pages/settings/pages/inboxes/pages/inboxesView/ui/InboxesTable/InboxesTable'
import { InboxesRoutes } from 'pages/settings/pages/inboxes/route/type'
import { getInboxesColumns } from './columns'

export const InboxesTab = observer(() => {
  const { inboxesViewStore, currentUser, isManageOrganizationInboxes } = useInboxesViewContext()
  const { tableStore, loading, search, setSearch, sortedRows, makeDefaultInbox } = inboxesViewStore

  useEffect(() => {
    return () => {
      setSearch('')
    }
  }, [])

  const isInboxOwner = (row: IRow<RowInbox>) => row.origin.ownerId === currentUser.id

  const columns = useMemo(
    () =>
      getInboxesColumns().map((col) => ({
        ...col,
        onCheckCellPointer: (row: IRow<RowInbox>) => {
          return isManageOrganizationInboxes || isInboxOwner(row)
        },
      })),
    [isManageOrganizationInboxes]
  )

  const getRowActions = (id: number) => {
    return [
      {
        text: 'Set as primary inbox',
        icon: 'flagFilled' as IIconsVector,
        onAction: () => makeDefaultInbox(id),
      },
    ]
  }

  const onRowClick = (row: IRow<RowInbox>) => {
    if (row.origin.disabled) {
      return
    }

    if (isManageOrganizationInboxes || isInboxOwner(row)) {
      uiStore.changeRouteViaVue({
        path: `/${InboxesPath.root}/${row.id}/${InboxesRoutes.members}`,
      })
    }
  }

  const rows = sortedRows.map((item) => {
    const isCurrentUserMember = item.origin.memberIds.includes(currentUser.id)

    item.setActionsProps({
      actions: isCurrentUserMember && !item.isDefaultTeam ? getRowActions(item.id) : [],
    })

    item.setDisabled(!!item.origin.disabled)

    return item
  })

  return (
    <InboxesTable<RowInbox>
      rows={rows}
      columns={columns}
      loading={loading}
      setSearch={setSearch}
      search={search}
      tableStore={tableStore}
      onRowClick={onRowClick}
    />
  )
})
